import React, { useEffect, useState } from 'react';
import { RideBar } from '../components/RideBar';
import { myRidesApi } from 'services/auth/rideApis';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';

export const MyRides = () => {
    const [rides, setRides] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Active');
    const navigator = useNavigate();

    useEffect(() => {
        const fetchMyRides = async () => {
            try {
                setLoading(true);
                toast.loading("Loading your rides...");
                const data = await myRidesApi();
                setRides(data);
                toast.dismiss();
            } catch (error) {
                console.error("Failed to fetch rides:", error);
                toast.dismiss();
                toast.error("Failed to load your rides");
                setRides([]);
            } finally {
                setLoading(false);
            }
        };

        fetchMyRides();
    }, []);

    const handleClick = (id) => {
        navigator(`/my-rides/${id}`);
    };

    const filteredRides = React.useMemo(() => {
        if (!rides) return [];
        return rides.filter((ride) => {
            if (activeTab === "Recurring") return !!ride.recurrenceGroupId;
            if (activeTab === "Active") return ride.status === "OPEN" || ride.status === "ONGOING";
            if (activeTab === "Completed") return ride.status === "COMPLETED";
            if (activeTab === "Cancelled") return ride.status === "CANCELLED";
            return true;
        }).sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [rides, activeTab]);

    if (loading) {
        return (
            <div className="hideScrollBars overflow-y-scroll flex-1 py-2 px-2 backdrop-blur z-10 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
                    <p className="mt-4 text-zinc-600 font-medium drop-shadow-sm">Loading your rides...</p>
                </div>
            </div>
        );
    }

    const tabs = ["Active", "Completed", "Cancelled", "Recurring"];

    return (
        <div className="hideScrollBars overflow-y-auto flex-1 py-8 px-4 backdrop-blur z-10 w-full">
            <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h1 className="text-3xl font-bold text-zinc-900 drop-shadow-sm">My Rides</h1>
                    <Button 
                        onClick={() => navigator("/create")}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-medium px-6 shadow-sm"
                    >
                        + Create New Ride
                    </Button>
                </div>

                <div className="flex flex-wrap gap-2 pb-2">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                                activeTab === tab 
                                ? "bg-zinc-900 text-white shadow-md" 
                                : "bg-white/80 text-zinc-600 hover:bg-white hover:text-zinc-900 shadow-sm"
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {rides?.length > 0 ? (
                    <div className="flex flex-col space-y-5">
                        {filteredRides.length > 0 ? (
                            filteredRides.map((ride, i) => (
                                <div key={ride?.id || i} onClick={() => handleClick(ride?.id)}>
                                    <RideBar ride={ride} />
                                </div>
                            ))
                        ) : (
                            <div className="flex items-center flex-col space-y-2 justify-center text-xl text-zinc-600 py-12">
                                <span className="bg-white/70 p-6 rounded-lg shadow-sm border border-white/50">
                                    No {activeTab.toLowerCase()} rides found.
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center flex-col space-y-4 justify-center text-2xl h-64">
                        <span className="bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-sm border border-white/50 text-zinc-800 font-medium">
                            No rides created yet
                        </span>
                        <Button onClick={() => navigator("/create")} className="bg-zinc-900 hover:bg-zinc-800 text-white">
                            Click here to create a ride
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};